<template>
    <div>
        <md-sidenav class="md-left" ref="leftSidenav" @open="open()" @close="close()">
            <md-toolbar class="md-large main-toolbar">
                <div class="md-toolbar-container ql-toolbar-logo">
                    <h1>
                        <span class="sr-only">Cuelist</span>
                        <img :src="require('../../../assets/img/cuelisttextWHITE.png')" alt="cuelisttext" class="logo-text" />
                        <img :src="require('../../../assets/img/cuelist.png')" alt="cuelist" class="logo" />
                    </h1>
                </div>
                <div class="md-toolbar-container ql-toolbar-avatar">
                    <md-avatar class="md-small">
                        <img :src="user.profile_picture" alt="People" v-if="user.profile_picture"/>
                        <img :src="require('../../../assets/img/silhouette.jpg')" v-else alt="People"/>
                    </md-avatar>
                    <div class="md-subtitle avatar-name-role" v-if="user.username">{{user.username}}</div>
                    <div class="md-subtitle avatar-name-role" v-else>Cuelist User</div>
                </div>
            </md-toolbar>
            <md-list>
                <router-link :to="{name: 'dashboard'}" tag="md-list-item">
                    <span @click="projectsClick()">All Projects</span>
                </router-link>
                <md-list-item v-if="project && project.id" class="md-list-item-project-title-lock">
                    <span><strong>{{project.title}}</strong></span>
                    <div class="project-lock-toggle" v-if="canToggleLock">
                        <md-switch v-model="localLocked" id="locked" name="locked" @change="toggleLocked">Project Lock</md-switch>
                    </div>
                </md-list-item>
                <md-list-item v-if="project && project.id">
                    <md-list>
                        <md-list-item @click="collaboratorsClick()">
                            <span>Collaborators</span>
                        </md-list-item>
                        <md-list-item @click="layersClick()">
                            <span>Layers</span>
                        </md-list-item>
                        <md-list-item @click="historyClick()">
                            <span>History</span>
                        </md-list-item>
                        <md-list-item @click="projectSettingsClick()">
                            <span>Project Settings</span>
                        </md-list-item>
                    </md-list>
                </md-list-item>
                <router-link tag="md-list-item" :to="{name: 'account'}">
                    <span>Account Settings</span>
                </router-link>
                <md-list-item>
                    <div class="md-button md-list-item-container">
                        <span>Help</span>
                    </div>
                    <md-button class="md-button-ghost" @click="helpClick"></md-button>
                </md-list-item>
                <md-list-item @click="logoutClick()">
                    <span>Log Out</span>
                </md-list-item>
            </md-list>
        </md-sidenav>
        <layers-nav v-if="project && project.id" @close="closeMainNav()" />
        <collaborators-nav v-if="project && project.id" @close="closeMainNav()" />
        <print-nav v-if="project && project.id" @close="closeMainNav()" />
        <project-settings-nav 
            :currentPage="currentPage" 
            :cuePlacement="getProjectCuePlacement()" 
            :projectduplicateSettingLocked="getlockedDuplicateSettingProject()"
            :collabDuplicateProjectLocked="getDuplicateProjectLocked()"
            :showMode="showMode" 
            v-if="project && project.id && user"
            @close="closeMainNav()"
        />
        <history-nav v-if="project && project.id" @close="closeMainNav()" />
    </div>
</template>

<script>
import { sessionDeleteAllowed } from '@/lib/session-watch';
import firebase from 'firebase/app';
import { mapGetters } from 'vuex';
import repos from '@/data';
import { canToggleProjectLock } from '@/lib/project-lock';
import NavBus from '@/buses/nav';
import LayersNav from '@/components/layers/nav';
import CollaboratorsNav from '@/components/collaborators/nav';
import HistoryNav from '@/components/history/versions-nav';
import PrintNav from '@/components/print/nav';
import ProjectSettingsNav from '@/components/project-settings/nav';

/**
 * CoreNavMain
 */
const CoreNavMain = {
    name: 'CoreNavMain',
    props: ['currentPage', 'showMode', 'locked'],
    data: function() {
        return {
            localLocked: this.$props.locked
        };
    },
    computed: {
        ...mapGetters([
            'project',
            'user'
        ]),
        // Owner only, with or without a paid plan (see lib/project-lock.js).
        canToggleLock() {
            return canToggleProjectLock(this.user, this.project);
        }
    },
    watch: {
        locked() {
            this.localLocked = this.locked;
        }
    },
    methods: {
        open() {
        },
        close() {
        },
        closeMainNav() {
            this.$refs.leftSidenav.close();
        },
        projectsClick() {
            this.closeMainNav();
        },
        collaboratorsClick() {
            this.closeMainNav();
            NavBus.$emit('collaborators-nav.open');
        },
        layersClick() {
            this.closeMainNav();
            NavBus.$emit('layers-nav.open');
        },
        historyClick() {
            this.closeMainNav();
            NavBus.$emit('versions-nav.open');
        },
        projectSettingsClick() {
            this.closeMainNav();
            NavBus.$emit('project-settings-nav.open');
        },
        printClick() {
            this.closeMainNav();
            NavBus.$emit('print-nav.open');
        },
        async logoutClick() {
            this.closeMainNav();
            const currentSessionID = localStorage.getItem('sessionID');
            const currentUser = firebase.auth().currentUser;
            const userId = currentUser && currentUser.uid;

            if (sessionDeleteAllowed(currentUser, userId, currentSessionID)) {
                await repos.users.update(userId, {
                    [`userDeviceSession.${currentSessionID}`]: firebase.firestore.FieldValue.delete()
                });
            }

            await firebase.auth().signOut();
            localStorage.removeItem('sessionID');
            this.$router.replace('/login');
        },
        helpClick() {
            // Security: Use anchor with rel="noopener" to prevent window.opener access
            // This prevents tabnabbing attacks where the new page could manipulate window.opener.location
            const link = document.createElement('a');
            link.href = 'https://thecuelist.com/user-guide';
            link.target = '_blank';
            link.rel = 'noopener noreferrer';
            link.click();
        },
        getlockedDuplicateSettingProject() {
            return (this.project && this.project.duplicateSetting) || false;
        },
        getProjectCuePlacement() {
            return (this.user && this.project && this.project.cuePlacement) || 'right';
        },
        getDuplicateProjectLocked() {
            return (this.user && this.project && this.project.collabDuplicateProjectLocked) || false;
        },
        toggleLocked() {
            this.$emit('toggleLocked');
        },
        handleMainNavOpen() {
            if (this.$refs.leftSidenav) {
                this.$refs.leftSidenav.open();
            }
        },
        handleMainNavOpenInterval(cb) {
            if (this.$refs.leftSidenav) {
                cb();
            }
        },
        handleMainNavOpenForce(cb) {
            const timer = setInterval(() => {
                NavBus.$emit('main-nav.open.interval', this.createMainNavIntervalCallback(timer, cb));
            }, 100);
        },
        createMainNavIntervalCallback(timer, cb) {
            return () => {
                clearInterval(timer);
                cb();
            };
        },
        handleProjectSettingsNavOpenForce(cb) {
            NavBus.$emit('main-nav.open.force', () => {
                this.openProjectSettingsNavInterval(cb);
            });
        },
        openProjectSettingsNavInterval(cb) {
            const timer = setInterval(() => {
                NavBus.$emit('project-settings-nav.open.interval', this.createProjectSettingsIntervalCallback(timer, cb));
            }, 100);
        },
        createProjectSettingsIntervalCallback(timer, cb) {
            return () => {
                clearInterval(timer);
                cb();
            };
        }
    },
    components: {
        'layers-nav': LayersNav,
        'collaborators-nav': CollaboratorsNav,
        'print-nav': PrintNav,
        'history-nav': HistoryNav,
        'project-settings-nav': ProjectSettingsNav
    },
    created: function() {
        NavBus.$on('main-nav.open', this.handleMainNavOpen);
        NavBus.$on('main-nav.open.interval', this.handleMainNavOpenInterval);
        NavBus.$on('main-nav.open.force', this.handleMainNavOpenForce);
        NavBus.$on('project-settings-nav.open.force', this.handleProjectSettingsNavOpenForce);
    },
    beforeDestroy: function() {
        NavBus.$off('main-nav.open');
        NavBus.$off('main-nav.open.interval');
        NavBus.$off('main-nav.open.force');
        NavBus.$off('project-settings-nav.open.force');
    }
};
export default CoreNavMain;
</script>

<style lang="scss">
.sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
}

.md-sidenav {
    .md-list {
        width: 100%;
        .md-list-item {
            cursor: pointer;
            border-bottom: 1px solid #ddd;
            box-sizing: border-box;
        }
        .md-list {
            .md-list-item {
                &:last-child {
                    border-bottom: none;
                }
            }
        }
    }
    .md-toolbar-container {
        text-align: center;
        height: auto;
        h1, h2 {
            width: 100%;
        }
    }
    .ql-toolbar-logo {
        h1 {
            margin: 0;
        }
        .logo-text {
            width: 90%;
        }
        .logo {
            width: 40%;
        }
    }
    .md-toolbar-container.ql-toolbar-avatar {
        margin: 0.5em 0;
        display: flex;
        .md-avatar {
            margin-right: 0.5rem;
            flex: 0;
        }
        .avatar-name-role {
            flex: 1;
            text-align: left;
            font-size: 20px;
        }
    }
}

.project-lock-toggle {
    .md-switch {
        &.md-checked {
            .md-switch-container {
                background-color: darken(#5ecfdf, 30%) !important;

                .md-switch-thumb {
                    background-color: #5ecfdf;
                }
            }
        }
        label {
            font-weight: 100;
        }
    }
}

.md-avatar.md-xlarge {
    width: 128px;
    min-width: 128px;
    height: 128px;
    min-height: 128px;
    border-radius: 128px;
}

.md-list-item-project-title-lock {
    .md-list-item-container {
        flex-direction: column;
        align-items: flex-start;
        span {
            line-height: 1.3;
            margin: 16px 0 0;
        }
    }
}
</style>


// WEBPACK FOOTER //
// main.vue?12c51a0e